/**
 * Which incident tasks belong to a roster seat's phase tabs.
 *
 * A seat shows both: a position-assigned task nobody has picked up, and a task
 * assigned directly to whoever is sitting there.
 */

/** Statuses from `ACTION_STATUS_OPTIONS`; the phase split is over these. */
export const PLANNING_STATUSES = ['Planned'] as const;
export const EXECUTION_STATUSES = [
  'Current/Ongoing',
  'Blocked',
  'Submitted for Review',
  'Approved',
] as const;

export type PositionPhase = 'planning' | 'execution';

const PHASE_STATUSES: Record<PositionPhase, readonly string[]> = {
  planning: PLANNING_STATUSES,
  execution: EXECUTION_STATUSES,
};

type PhaseTask = {
  assignee_kind?: string | null;
  assignee_id?: string | null;
  poc?: string | null;
  status?: string | null;
  archived?: boolean | number | null;
};

type PhaseMember = {
  userId?: string | null;
};

/**
 * The user ids sitting in the seat, across the active and draft rosters.
 *
 * Draft is included because the tabs are a planning surface: a seat being
 * handed over in the next operational period should show the incoming person's
 * work, not hide it until the roster is published. Ids are deduplicated since
 * the same person routinely holds the seat in both rosters.
 */
export function positionOccupantIds(
  ...memberLists: (readonly PhaseMember[] | null | undefined)[]
): Set<string> {
  const ids = new Set<string>();
  for (const members of memberLists) {
    for (const member of members ?? []) {
      if (member?.userId) ids.add(member.userId);
    }
  }
  return ids;
}

/**
 * True when this task is assigned to one of the given people.
 *
 * `poc` alone counts as a user assignment: actions written before the assignee
 * model existed carry only that field, the same fallback
 * `resolveActionAssignee` makes.
 */
export function isAssignedToUsers(
  task: PhaseTask,
  userIds: ReadonlySet<string>
): boolean {
  const kind = task.assignee_kind ?? (task.poc ? 'user' : null);
  if (kind !== 'user') return false;
  const id = task.assignee_id ?? task.poc ?? null;
  return !!id && userIds.has(id);
}

/** True when this task is assigned to the roster seat itself. */
export function isAssignedToPosition(
  task: PhaseTask,
  positionId?: string | null
): boolean {
  return (
    !!positionId &&
    task.assignee_kind === 'position' &&
    task.assignee_id === positionId
  );
}

/**
 * The seat's tasks for one phase.
 *
 * `Closed` falls out of both phases by construction, so a finished task stops
 * occupying either tab, and archived tasks never appear.
 */
export function positionPhaseTasks<T extends PhaseTask>(
  tasks: readonly T[],
  userIds: ReadonlySet<string>,
  phase: PositionPhase,
  positionId?: string | null
): T[] {
  const statuses = PHASE_STATUSES[phase];
  return tasks.filter(
    (task) =>
      !task.archived &&
      (isAssignedToUsers(task, userIds) ||
        isAssignedToPosition(task, positionId)) &&
      !!task.status &&
      statuses.includes(task.status)
  );
}

/** Status a task gets when created from a phase tab. */
export function phaseDefaultStatus(phase: PositionPhase): string {
  return phase === 'planning' ? 'Planned' : 'Current/Ongoing';
}

/**
 * Who a task created from this tab should be assigned to.
 *
 * Only prefilled for a seat with exactly one occupant, where the intent is
 * unambiguous. With several people in the seat, guessing one would quietly put
 * work on the wrong person, so the drawer's picker is left empty.
 */
export function phaseDefaultAssigneeId(
  userIds: ReadonlySet<string>
): string | null {
  if (userIds.size !== 1) return null;
  const [only] = userIds;
  return only ?? null;
}
